import React, { useEffect, useMemo, useState } from 'react';
import styled, { keyframes, css } from 'styled-components';
import { getTranslation, Language } from '../translations';
import { useLanguage } from './Header';
import { motion, useMotionValue, useTransform, PanInfo, useMotionValueEvent, AnimatePresence } from 'framer-motion';
import Confetti from 'react-confetti';

export type HowItWorksSlide = {
  image?: string;
  illustration?: React.ReactNode;
  title: string;
  description: string;
  helper?: string;
  details?: string[];
};

interface HowItWorksModalProps {
  isOpen: boolean;
  onClose: () => void;
  slides?: HowItWorksSlide[];
}

// Animations
const overlayIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const overlayOut = keyframes`
  from { opacity: 1; }
  to { opacity: 0; }
`;
const dialogIn = keyframes`
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
`;
const dialogOut = keyframes`
  from { opacity: 1; transform: scale(1); }
  to { opacity: 0; transform: scale(0.95); }
`;
const sheetIn = keyframes`
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
`;
const sheetOut = keyframes`
  from { transform: translateY(0); }
  to { transform: translateY(100%); }
`;

const Overlay = styled.div<{ $closing?: boolean }>`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  z-index: 10000;
  overflow: hidden;
  will-change: opacity;
  animation: ${({ $closing }) => $closing ? overlayOut : overlayIn} 0.3s ease forwards;

  @media (max-width: 768px) {
    padding: 0;
    align-items: flex-end;
  }
`;

const ModalCard = styled.div<{ $closing?: boolean }>`
  width: min(850px, 92%);
  height: 82vh;
  min-height: 580px;
  max-height: 82vh;
  background: #ffffff;
  border-radius: 20px;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid rgba(226, 232, 240, 0.8);
  will-change: transform, opacity;
  animation: ${({ $closing }) => $closing ? dialogOut : dialogIn} 0.3s cubic-bezier(0.4, 0, 0.2, 1) forwards;

  @media (max-width: 768px) {
    animation: ${({ $closing }) => $closing ? sheetOut : sheetIn} 0.35s cubic-bezier(0.4, 0, 0.2, 1) forwards;
  }
  
  .dark & {
    background: #0f172a;
    border-color: #1e293b;
    box-shadow: 0 25px 80px rgba(0, 0, 0, 0.7),
                0 0 0 1px rgba(30, 41, 59, 0.5) inset;
  }
  
  @media (max-width: 968px) {
    width: min(800px, 92%);
    height: 80vh;
    min-height: 560px;
    max-height: 80vh;
  }
  
  @media (max-width: 768px) {
    width: 100%;
    height: 85vh;
    min-height: auto;
    max-height: 85vh;
    border-radius: 24px 24px 0 0;
    border-bottom: none;
  }
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.75rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(to bottom, #ffffff 0%, #fafbfc 100%);
  flex-shrink: 0;
  
  .dark & {
    background: linear-gradient(to bottom, #0f172a 0%, #1a1f2e 100%);
    border-bottom-color: #1e293b;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    position: relative;
    padding-bottom: 1.25rem;
    cursor: grab;
    
    &:active {
      cursor: grabbing;
    }
    
    &::after {
      content: '';
      position: absolute;
      bottom: 0.5rem;
      left: 50%;
      transform: translateX(-50%);
      width: 40px;
      height: 4px;
      background: #d1d5db;
      border-radius: 2px;
    }
  }
`;

const Heading = styled.h2`
  margin: 0;
  font-size: 1.875rem;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.025em;
  
  .dark & {
    color: #f9fafb;
  }
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
    font-weight: 700;
  }
`;

const CloseButton = styled.button`
  appearance: none;
  width: 38px;
  height: 38px;
  border-radius: 10px;
  border: 1px solid #e5e7eb;
  background: #ffffff;
  color: #6b7280;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  &:hover {
    background: #f9fafb;
    color: #374151;
    border-color: #d1d5db;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
  }
  
  .dark & {
    background: #1e293b;
    border-color: #334155;
    color: #9ca3af;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
    
    &:hover {
      background: #293548;
      color: #d1d5db;
      border-color: #475569;
    }
  }
`;

const SlideContainer = styled.div`
  position: relative;
  flex: 1;
  overflow-y: auto;
  overflow-x: hidden;
  display: flex;
  min-height: 0;
  height: 100%;
  background: #ffffff;
  
  .dark & {
    background: #0f172a;
  }
  
  @media (max-width: 768px) {
    -webkit-overflow-scrolling: touch;
    overscroll-behavior: contain;
    overscroll-behavior-y: contain;
    /* Prevent pull-to-refresh on mobile */
    overscroll-behavior-block: contain;
  }
`;

const SlideContent = styled.div`
  width: 100%;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 0;
  min-height: 100%;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    min-height: 100%;
  }
`;

const ImageSection = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2.25rem 1.75rem;
  background: #f9fafb;
  border-right: 1px solid #e5e7eb;
  flex-shrink: 0;
  height: 100%;
  min-height: 380px;
  
  .dark & {
    background: #1e293b;
    border-right-color: #334155;
  }

  @media (max-width: 968px) {
    padding: 2rem 1.5rem;
    border-right: none;
    border-bottom: 1px solid #e5e7eb;
    min-height: 180px;
    height: auto;
    
    .dark & {
      border-bottom-color: #334155;
    }
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.875rem;
    min-height: 130px;
    border-bottom: 1px solid #e5e7eb;
    
    .dark & {
      border-bottom-color: #334155;
    }
  }
`;

const ImageWrapper = styled.div`
  position: relative;
  width: 100%;
  max-width: 320px;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;
  flex-shrink: 0;
  
  @media (max-width: 968px) {
    max-width: 280px;
  }
  
  @media (max-width: 768px) {
    max-width: 140px;
  }
`;

const Image = styled.img`
  width: 100%;
  height: auto;
  max-height: 300px;
  object-fit: contain;
  
  @media (max-width: 968px) {
    max-height: 200px;
  }
  
  @media (max-width: 768px) {
    max-height: 110px;
  }
`;

const ContentSection = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  padding: 2.25rem 1.75rem;
  background: #ffffff;
  overflow-y: auto;
  overflow-x: hidden;
  min-height: 0;
  height: 100%;
  
  .dark & {
    background: #0f172a;
  }
  
  @media (max-width: 968px) {
    padding: 2rem 1.5rem;
    text-align: center;
    align-items: center;
    justify-content: flex-start;
    height: auto;
  }
  
  @media (max-width: 768px) {
    padding: 1rem 0.875rem;
    text-align: left;
    align-items: flex-start;
    overflow-y: visible;
    flex: 1;
  }
`;

const StepBadge = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.625rem 1.125rem;
  background: #111827;
  color: #ffffff;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  width: fit-content;
  margin-bottom: 1.25rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  border: 1px solid transparent;
  box-shadow: 0 2px 8px rgba(17, 24, 39, 0.2);
  flex-shrink: 0;
  
  .dark & {
    background: #1e293b;
    color: #f8fafc;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 968px) {
    margin: 0 auto 1.25rem;
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.75rem;
    font-size: 0.65rem;
    margin-bottom: 0.75rem;
    margin-left: 0;
    margin-right: auto;
  }
`;

const Title = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 2rem;
  font-weight: 800;
  color: #111827;
  letter-spacing: -0.04em;
  line-height: 1.2;
  
  .dark & {
    color: #f9fafb;
  }
  
  @media (max-width: 768px) {
    font-size: 1.125rem;
    margin-bottom: 0.5rem;
    line-height: 1.3;
    font-weight: 700;
  }
`;

const DetailsList = styled.ul`
  margin: 1rem 0 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 0.625rem;
  max-width: 480px;
  
  @media (max-width: 968px) {
    max-width: none;
  }
  
  @media (max-width: 768px) {
    margin-top: 0.625rem;
    gap: 0.4rem;
    width: 100%;
  }
`;

const DetailItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.625rem;
  font-size: 0.9375rem;
  line-height: 1.6;
  color: #4b5563;
  
  .dark & {
    color: #d1d5db;
  }
  
  @media (max-width: 768px) {
    font-size: 0.75rem;
    line-height: 1.4;
    gap: 0.45rem;
  }
`;

const DetailIcon = styled.div`
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  border-radius: 50%;
  background: #111827;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 0.125rem;
  box-shadow: 0 2px 6px rgba(17, 24, 39, 0.2);
  
  svg {
    width: 11px;
    height: 11px;
    color: white;
  }
  
  .dark & {
    background: #1e293b;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.4);
  }
  
  @media (max-width: 768px) {
    width: 16px;
    height: 16px;
    margin-top: 0.05rem;
    
    svg {
      width: 9px;
      height: 9px;
    }
  }
`;

const Description = styled.p`
  margin: 0 0 1rem 0;
  font-size: 0.9375rem;
  line-height: 1.65;
  color: #4b5563;
  max-width: 480px;
  font-weight: 400;
  
  .dark & {
    color: #d1d5db;
  }
  
  @media (max-width: 968px) {
    max-width: none;
  }
  
  @media (max-width: 768px) {
    font-size: 0.8125rem;
    margin-bottom: 0.625rem;
    line-height: 1.5;
  }
`;

const HelperText = styled.div`
  padding: 0.875rem 1rem;
  background: #f1f5f9;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  font-size: 0.8125rem;
  font-weight: 700;
  color: #111827;
  width: fit-content;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  flex-shrink: 0;
  
  .dark & {
    background: #1e293b;
    border-color: #334155;
    color: #e2e8f0;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.3);
  }
  
  @media (max-width: 968px) {
    margin: 1rem auto 0;
  }
  
  @media (max-width: 768px) {
    padding: 0.625rem 0.75rem;
    font-size: 0.7rem;
    margin-top: 0.75rem;
    margin-left: 0;
    margin-right: auto;
    width: auto;
    max-width: 100%;
    gap: 0.4rem;
    
    svg {
      width: 14px;
      height: 14px;
    }
  }
`;

const NavigationContainer = styled.div`
  padding: 1.25rem 1.75rem;
  border-top: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.5rem;
  background: linear-gradient(to top, #ffffff 0%, #fafbfc 100%);
  flex-shrink: 0;
  
  .dark & {
    background: linear-gradient(to top, #0f172a 0%, #1a1f2e 100%);
    border-top-color: #1e293b;
  }
  
  @media (max-width: 768px) {
    padding: 0.875rem 1rem;
    flex-direction: column;
    gap: 0.75rem;
    border-top: 1px solid #e5e7eb;
    background: #ffffff;
    
    .dark & {
      background: #0f172a;
      border-top-color: #1e293b;
    }
  }
`;

const StepIndicators = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  
  @media (max-width: 768px) {
    width: 100%;
  justify-content: center;
  }
`;

const StepDot = styled.button<{ $active: boolean }>`
  appearance: none;
  width: ${({ $active }) => ($active ? '44px' : '8px')};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${({ $active }) => ($active
    ? '#111827'
    : '#d1d5db')};
  cursor: pointer;
  transition: all 0.25s ease;
  box-shadow: ${({ $active }) => ($active
    ? '0 2px 8px rgba(17, 24, 39, 0.25)'
    : 'none')};

  &:hover {
    background: ${({ $active }) => ($active
    ? '#111827'
    : '#9ca3af')};
  }
  
  .dark & {
    background: ${({ $active }) => ($active
    ? '#ffffff'
    : '#4b5563')};
    box-shadow: ${({ $active }) => ($active
    ? '0 2px 8px rgba(255, 255, 255, 0.2)'
    : 'none')};
    
    &:hover {
      background: ${({ $active }) => ($active
    ? '#ffffff'
    : '#6b7280')};
    }
  }
`;

const NavButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    width: 100%;
    justify-content: space-between;
  }
`;

const NavButton = styled.button<{ $disabled?: boolean; $primary?: boolean }>`
  appearance: none;
  padding: 0.9375rem 2rem;
  border-radius: 10px;
  border: none;
  font-size: 0.9375rem;
  font-weight: 700;
  cursor: ${({ $disabled }) => ($disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  box-shadow: ${({ $disabled, $primary }) => ($disabled || !$primary ? 'none' : '0 4px 12px rgba(17, 24, 39, 0.25)')};
  
  ${({ $disabled, $primary }) => {
    if ($disabled) {
      return `
        background: #f3f4f6;
        color: #9ca3af;
        opacity: 0.5;
      `;
    }

    if ($primary) {
      return `
        background: #111827;
        color: white;
        
        &:hover {
          background: #000000;
          box-shadow: 0 6px 16px rgba(17, 24, 39, 0.35);
          transform: translateY(-1px);
        }
        
        &:active {
          transform: translateY(0);
        }
      `;
    }

    return `
      background: white;
      color: #111827;
      border: 1.5px solid #e5e7eb;
      
      &:hover {
        background: #f9fafb;
        border-color: #d1d5db;
      }
    `;
  }}
  
  .dark & {
    ${({ $disabled, $primary }) => {
    if ($disabled) {
      return `
          background: #1f2937;
          color: #6b7280;
        `;
    }

    if ($primary) {
      return `
          background: #ffffff;
          color: #111827;
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
          
          &:hover {
            background: #f9fafb;
            box-shadow: 0 6px 16px rgba(0, 0, 0, 0.4);
          }
        `;
    }

    return `
        background: #1e293b;
        color: #e2e8f0;
        border: 1.5px solid #334155;
        
        &:hover {
          background: #334155;
          border-color: #475569;
        }
      `;
  }}
  }
  
  @media (max-width: 768px) {
    flex: 1;
    justify-content: center;
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    min-height: 44px;
    
    svg {
      width: 14px;
      height: 14px;
  }
`;

/* ── Phone mockup illustrations showing Phone Bros UI per step ── */
const D = '#111827';  // donker
const M = '#6b7280';  // muted
const B = '#e5e7eb';  // border
const F = '#f3f4f6';  // fill
const tx: React.CSSProperties = { fontFamily: '-apple-system,BlinkMacSystemFont,system-ui,sans-serif', margin: 0, lineHeight: 1 };

/* Phone: real iPhone SVG frame + HTML screen content overlay — fully responsive */
const PhoneWrap = styled.div`
  position: relative;
  width: 194px;
  max-width: 100%;
  user-select: none;
  filter: drop-shadow(0 2px 6px rgba(0,0,0,0.06)) drop-shadow(0 12px 32px rgba(0,0,0,0.14));

  @media (max-width: 968px) {
    width: 160px;
  }
  @media (max-width: 768px) {
    width: 120px;
  }
`;
const phoneFrame: React.CSSProperties = { width: '100%', height: 'auto', display: 'block' };
const ScreenOverlay = styled.div`
  position: absolute;
  top: 7.4%; left: 2.69%; width: 94.62%; height: 91.2%;
  border-radius: 0 0 13.8% 13.8%;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background: #fff;
`;
const ScreenPad = styled.div`
  padding: 8px 11px 14px;
  display: flex;
  flex-direction: column;
  gap: 7px;
  flex: 1;
  font-size: 8px;

  @media (max-width: 968px) {
    padding: 6px 8px 10px;
    gap: 5px;
    font-size: 7px;
  }
  @media (max-width: 768px) {
    padding: 4px 6px 8px;
    gap: 4px;
    font-size: 5.5px;
  }
`;
/* Responsive text sizes: use em so everything scales with ScreenPad font-size */
const t11: React.CSSProperties = { ...tx, fontSize: '1.375em', fontWeight: 800, color: D, letterSpacing: '-0.3px' };
const t10: React.CSSProperties = { ...tx, fontSize: '1.25em', fontWeight: 700, color: D };
const t9: React.CSSProperties = { ...tx, fontSize: '1.125em', fontWeight: 700 };
const t8: React.CSSProperties = { ...tx, fontSize: '1em', fontWeight: 700, color: D };
const t7: React.CSSProperties = { ...tx, fontSize: '0.875em', color: M };
const t6: React.CSSProperties = { ...tx, fontSize: '0.8em', color: '#9ca3af' };

/* Teksten binnen de mock-telefoonschermen (illustraties). Per taal volledig ingevuld. */
interface MockTexts {
  chooseDevice: string;
  whatToSell: string;
  searchModel: string;
  modelsAvailable: string;
  continueLabel: string;
  stepTwoOfFour: string;
  screenCondition: string;
  howScreenLooks: string;
  noScratches: string;
  screenPerfect: string;
  lightScratches: string;
  lightUsage: string;
  visibleDamage: string;
  cracksDeepScratches: string;
  estimatedValue: string;
  cart: string;
  twoDevices: string;
  excellent: string;
  addAnotherDevice: string;
  subtotal: string;
  total: string;
  makeAppointment: string;
  paymentSuccessful: string;
  paidToAccount: string;
  orderNumber: string;
  method: string;
  directPayout: string;
  speed: string;
  sameDay: string;
  emailConfirmation: string;
  homeButton: string;
  shareButton: string;
}

const MOCK_TEXTS: Record<Language, MockTexts> = {
  nl: {
    chooseDevice: 'Kies je toestel',
    whatToSell: 'Wat wil je verkopen?',
    searchModel: 'Zoek je model...',
    modelsAvailable: '50+ modellen beschikbaar',
    continueLabel: 'Ga verder',
    stepTwoOfFour: 'Stap 2 van 4',
    screenCondition: 'Schermconditie',
    howScreenLooks: 'Hoe ziet je scherm eruit?',
    noScratches: 'Geen krassen',
    screenPerfect: 'Scherm is in perfecte staat',
    lightScratches: 'Lichte krassen',
    lightUsage: 'Kleine gebruikssporen',
    visibleDamage: 'Zichtbare schade',
    cracksDeepScratches: 'Barsten of diepe krassen',
    estimatedValue: 'Geschatte waarde',
    cart: 'Winkelwagen',
    twoDevices: '2 toestellen',
    excellent: 'Uitstekend',
    addAnotherDevice: 'Nog een toestel toevoegen',
    subtotal: 'Subtotaal',
    total: 'Totaal',
    makeAppointment: 'Afspraak maken',
    paymentSuccessful: 'Betaling geslaagd',
    paidToAccount: 'Overgemaakt naar je rekening',
    orderNumber: 'Ordernr.',
    method: 'Methode',
    directPayout: 'Directe uitbetaling',
    speed: 'Snelheid',
    sameDay: 'Zelfde dag',
    emailConfirmation: 'Bevestiging verstuurd per email',
    homeButton: 'Home',
    shareButton: 'Delen',
  },
  en: {
    chooseDevice: 'Choose your device',
    whatToSell: 'What do you want to sell?',
    searchModel: 'Search your model...',
    modelsAvailable: '50+ models available',
    continueLabel: 'Continue',
    stepTwoOfFour: 'Step 2 of 4',
    screenCondition: 'Screen condition',
    howScreenLooks: 'How does your screen look?',
    noScratches: 'No scratches',
    screenPerfect: 'Screen is in perfect condition',
    lightScratches: 'Light scratches',
    lightUsage: 'Minor signs of use',
    visibleDamage: 'Visible damage',
    cracksDeepScratches: 'Cracks or deep scratches',
    estimatedValue: 'Estimated value',
    cart: 'Cart',
    twoDevices: '2 devices',
    excellent: 'Excellent',
    addAnotherDevice: 'Add another device',
    subtotal: 'Subtotal',
    total: 'Total',
    makeAppointment: 'Make an appointment',
    paymentSuccessful: 'Payment successful',
    paidToAccount: 'Transferred to your account',
    orderNumber: 'Order no.',
    method: 'Method',
    directPayout: 'Direct payout',
    speed: 'Speed',
    sameDay: 'Same day',
    emailConfirmation: 'Confirmation sent by email',
    homeButton: 'Home',
    shareButton: 'Share',
  },
  fr: {
    chooseDevice: 'Choisissez votre appareil',
    whatToSell: 'Que voulez-vous vendre ?',
    searchModel: 'Recherchez votre modèle...',
    modelsAvailable: '50+ modèles disponibles',
    continueLabel: 'Continuer',
    stepTwoOfFour: 'Étape 2 sur 4',
    screenCondition: "État de l'écran",
    howScreenLooks: 'Dans quel état est votre écran ?',
    noScratches: 'Aucune rayure',
    screenPerfect: "L'écran est en parfait état",
    lightScratches: 'Légères rayures',
    lightUsage: "Petites traces d'usure",
    visibleDamage: 'Dommages visibles',
    cracksDeepScratches: 'Fissures ou rayures profondes',
    estimatedValue: 'Valeur estimée',
    cart: 'Panier',
    twoDevices: '2 appareils',
    excellent: 'Excellent',
    addAnotherDevice: 'Ajouter un autre appareil',
    subtotal: 'Sous-total',
    total: 'Total',
    makeAppointment: 'Prendre rendez-vous',
    paymentSuccessful: 'Paiement réussi',
    paidToAccount: 'Viré sur votre compte',
    orderNumber: 'N° de commande',
    method: 'Méthode',
    directPayout: 'Virement immédiat',
    speed: 'Rapidité',
    sameDay: 'Le jour même',
    emailConfirmation: 'Confirmation envoyée par e-mail',
    homeButton: 'Domicile',
    shareButton: 'Partager',
  },
  de: {
    chooseDevice: 'Wähle dein Gerät',
    whatToSell: 'Was möchtest du verkaufen?',
    searchModel: 'Suche dein Modell...',
    modelsAvailable: '50+ Modelle verfügbar',
    continueLabel: 'Weiter',
    stepTwoOfFour: 'Schritt 2 von 4',
    screenCondition: 'Displayzustand',
    howScreenLooks: 'Wie sieht dein Display aus?',
    noScratches: 'Keine Kratzer',
    screenPerfect: 'Display ist in perfektem Zustand',
    lightScratches: 'Leichte Kratzer',
    lightUsage: 'Kleine Gebrauchsspuren',
    visibleDamage: 'Sichtbare Schäden',
    cracksDeepScratches: 'Risse oder tiefe Kratzer',
    estimatedValue: 'Geschätzter Wert',
    cart: 'Warenkorb',
    twoDevices: '2 Geräte',
    excellent: 'Ausgezeichnet',
    addAnotherDevice: 'Weiteres Gerät hinzufügen',
    subtotal: 'Zwischensumme',
    total: 'Summe',
    makeAppointment: 'Termin vereinbaren',
    paymentSuccessful: 'Zahlung erfolgreich',
    paidToAccount: 'Auf dein Konto überwiesen',
    orderNumber: 'Bestellnr.',
    method: 'Methode',
    directPayout: 'Sofortauszahlung',
    speed: 'Geschwindigkeit',
    sameDay: 'Am selben Tag',
    emailConfirmation: 'Bestätigung per E-Mail gesendet',
    homeButton: 'Zuhause',
    shareButton: 'Teilen',
  },
  es: {
    chooseDevice: 'Elige tu dispositivo',
    whatToSell: '¿Qué quieres vender?',
    searchModel: 'Busca tu modelo...',
    modelsAvailable: '50+ modelos disponibles',
    continueLabel: 'Continuar',
    stepTwoOfFour: 'Paso 2 de 4',
    screenCondition: 'Estado de la pantalla',
    howScreenLooks: '¿Cómo está tu pantalla?',
    noScratches: 'Sin arañazos',
    screenPerfect: 'La pantalla está en perfecto estado',
    lightScratches: 'Arañazos leves',
    lightUsage: 'Pequeñas marcas de uso',
    visibleDamage: 'Daños visibles',
    cracksDeepScratches: 'Grietas o arañazos profundos',
    estimatedValue: 'Valor estimado',
    cart: 'Carrito',
    twoDevices: '2 dispositivos',
    excellent: 'Excelente',
    addAnotherDevice: 'Añadir otro dispositivo',
    subtotal: 'Subtotal',
    total: 'Total',
    makeAppointment: 'Concertar cita',
    paymentSuccessful: 'Pago realizado',
    paidToAccount: 'Transferido a tu cuenta',
    orderNumber: 'Nº de pedido',
    method: 'Método',
    directPayout: 'Pago directo',
    speed: 'Rapidez',
    sameDay: 'Mismo día',
    emailConfirmation: 'Confirmación enviada por correo',
    homeButton: 'Domicilio',
    shareButton: 'Compartir',
  },
  tr: {
    chooseDevice: 'Cihazınızı seçin',
    whatToSell: 'Ne satmak istiyorsunuz?',
    searchModel: 'Modelinizi arayın...',
    modelsAvailable: '50+ model mevcut',
    continueLabel: 'Devam et',
    stepTwoOfFour: '2 / 4 Adım',
    screenCondition: 'Ekran durumu',
    howScreenLooks: 'Ekranınız nasıl görünüyor?',
    noScratches: 'Çizik yok',
    screenPerfect: 'Ekran mükemmel durumda',
    lightScratches: 'Hafif çizikler',
    lightUsage: 'Küçük kullanım izleri',
    visibleDamage: 'Görünür hasar',
    cracksDeepScratches: 'Çatlaklar veya derin çizikler',
    estimatedValue: 'Tahmini değer',
    cart: 'Sepet',
    twoDevices: '2 cihaz',
    excellent: 'Mükemmel',
    addAnotherDevice: 'Başka bir cihaz ekle',
    subtotal: 'Ara toplam',
    total: 'Toplam',
    makeAppointment: 'Randevu alın',
    paymentSuccessful: 'Ödeme başarılı',
    paidToAccount: 'Hesabınıza aktarıldı',
    orderNumber: 'Sipariş no.',
    method: 'Yöntem',
    directPayout: 'Anında ödeme',
    speed: 'Hız',
    sameDay: 'Aynı gün',
    emailConfirmation: 'Onay e-posta ile gönderildi',
    homeButton: 'Eve',
    shareButton: 'Paylaş',
  },
  bg: {
    chooseDevice: 'Изберете устройството си',
    whatToSell: 'Какво искате да продадете?',
    searchModel: 'Търсете модела си...',
    modelsAvailable: '50+ модела налични',
    continueLabel: 'Продължете',
    stepTwoOfFour: 'Стъпка 2 от 4',
    screenCondition: 'Състояние на екрана',
    howScreenLooks: 'Как изглежда екранът ви?',
    noScratches: 'Без драскотини',
    screenPerfect: 'Екранът е в перфектно състояние',
    lightScratches: 'Леки драскотини',
    lightUsage: 'Леки следи от употреба',
    visibleDamage: 'Видими повреди',
    cracksDeepScratches: 'Пукнатини или дълбоки драскотини',
    estimatedValue: 'Прогнозна стойност',
    cart: 'Количка',
    twoDevices: '2 устройства',
    excellent: 'Отлично',
    addAnotherDevice: 'Добавете още едно устройство',
    subtotal: 'Междинна сума',
    total: 'Общо',
    makeAppointment: 'Запазете час',
    paymentSuccessful: 'Плащането е успешно',
    paidToAccount: 'Преведено към вашата сметка',
    orderNumber: '№ на поръчка',
    method: 'Метод',
    directPayout: 'Незабавно плащане',
    speed: 'Скорост',
    sameDay: 'В същия ден',
    emailConfirmation: 'Потвърждението е изпратено по имейл',
    homeButton: 'У дома',
    shareButton: 'Споделяне',
  },
  ro: {
    chooseDevice: 'Alegeți dispozitivul',
    whatToSell: 'Ce doriți să vindeți?',
    searchModel: 'Căutați modelul...',
    modelsAvailable: '50+ modele disponibile',
    continueLabel: 'Continuați',
    stepTwoOfFour: 'Pasul 2 din 4',
    screenCondition: 'Starea ecranului',
    howScreenLooks: 'Cum arată ecranul dvs.?',
    noScratches: 'Fără zgârieturi',
    screenPerfect: 'Ecranul este în stare perfectă',
    lightScratches: 'Zgârieturi ușoare',
    lightUsage: 'Urme mici de utilizare',
    visibleDamage: 'Deteriorări vizibile',
    cracksDeepScratches: 'Fisuri sau zgârieturi adânci',
    estimatedValue: 'Valoare estimată',
    cart: 'Coș',
    twoDevices: '2 dispozitive',
    excellent: 'Excelent',
    addAnotherDevice: 'Adăugați încă un dispozitiv',
    subtotal: 'Subtotal',
    total: 'Total',
    makeAppointment: 'Programați o întâlnire',
    paymentSuccessful: 'Plată reușită',
    paidToAccount: 'Transferat în contul dvs.',
    orderNumber: 'Nr. comandă',
    method: 'Metodă',
    directPayout: 'Plată directă',
    speed: 'Viteză',
    sameDay: 'În aceeași zi',
    emailConfirmation: 'Confirmare trimisă prin e-mail',
    homeButton: 'Acasă',
    shareButton: 'Partajare',
  },
  sk: {
    chooseDevice: 'Vyberte si zariadenie',
    whatToSell: 'Čo chcete predať?',
    searchModel: 'Vyhľadajte svoj model...',
    modelsAvailable: '50+ modelov k dispozícii',
    continueLabel: 'Pokračovať',
    stepTwoOfFour: 'Krok 2 z 4',
    screenCondition: 'Stav displeja',
    howScreenLooks: 'Ako vyzerá váš displej?',
    noScratches: 'Žiadne škrabance',
    screenPerfect: 'Displej je v perfektnom stave',
    lightScratches: 'Ľahké škrabance',
    lightUsage: 'Malé stopy používania',
    visibleDamage: 'Viditeľné poškodenie',
    cracksDeepScratches: 'Praskliny alebo hlboké škrabance',
    estimatedValue: 'Odhadovaná hodnota',
    cart: 'Košík',
    twoDevices: '2 zariadenia',
    excellent: 'Výborný',
    addAnotherDevice: 'Pridať ďalšie zariadenie',
    subtotal: 'Medzisúčet',
    total: 'Celkom',
    makeAppointment: 'Dohodnúť termín',
    paymentSuccessful: 'Platba bola úspešná',
    paidToAccount: 'Prevedené na váš účet',
    orderNumber: 'Č. objednávky',
    method: 'Metóda',
    directPayout: 'Okamžitá výplata',
    speed: 'Rýchlosť',
    sameDay: 'V ten istý deň',
    emailConfirmation: 'Potvrdenie odoslané e-mailom',
    homeButton: 'Domov',
    shareButton: 'Zdieľať',
  },
};

const tile = (label: string, sel = false): React.ReactNode => (
  <div style={{ background: sel ? F : '#fff', border: sel ? `2px solid ${D}` : `1.5px solid ${B}`, borderRadius: '1.4em', padding: '1.2em 0.5em 1em', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '0.6em', position: 'relative' as const }}>
    <div style={{ width: '2.2em', height: '3.4em', borderRadius: '0.6em', border: `1.5px solid ${sel ? D : '#9ca3af'}` }} />
    <p style={{ ...t8, fontWeight: 600, color: sel ? D : M }}>{label}</p>
    {sel && <div style={{ position: 'absolute' as const, top: '-0.7em', right: '-0.7em', width: '2em', height: '2em', borderRadius: '50%', background: D, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '2px solid #fff' }}>
      <svg width="1.2em" height="1.2em" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
    </div>}
  </div>
);

const opt = (label: string, sub: string, sel = false): React.ReactNode => (
  <div style={{ background: sel ? F : '#fff', border: sel ? `2px solid ${D}` : `1.5px solid ${B}`, borderRadius: '1.4em', padding: '1em 1.2em', display: 'flex', gap: '0.9em', alignItems: 'center' }}>
    <div style={{ width: '2.2em', height: '2.2em', borderRadius: '50%', background: sel ? D : '#fff', border: sel ? 'none' : `2px solid #d1d5db`, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      {sel && <svg width="1.2em" height="1.2em" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>}
    </div>
    <div>
      <p style={{ ...tx, fontSize: '1.06em', fontWeight: 600, color: sel ? D : '#374151' }}>{label}</p>
      <p style={{ ...t6, marginTop: '0.2em' }}>{sub}</p>
    </div>
  </div>
);

const cartItem = (name: string, spec: string, price: string): React.ReactNode => (
  <div style={{ background: '#fff', border: `1.5px solid ${B}`, borderRadius: '1.4em', padding: '1em 1.2em', display: 'flex', gap: '0.9em', alignItems: 'center' }}>
    <div style={{ width: '3.8em', height: '3.8em', borderRadius: '0.9em', background: F, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <div style={{ width: '1.6em', height: '2.6em', borderRadius: '0.4em', border: `1.5px solid ${D}` }} />
    </div>
    <div style={{ flex: 1 }}>
      <p style={t8}>{name}</p>
      <p style={{ ...t6, marginTop: '0.2em' }}>{spec}</p>
    </div>
    <p style={{ ...t9, fontWeight: 800, color: D }}>{price}</p>
  </div>
);

const AnimatedStep1Illustration = ({ mock }: { mock: MockTexts }) => (
  <PhoneWrap>
    <img src="/illustrations/iphone-frame.svg" alt="" style={phoneFrame} draggable={false} />
    <ScreenOverlay>
      <ScreenPad>
        <p style={t11}>{mock.chooseDevice}</p>
        <p style={t7}>{mock.whatToSell}</p>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6em', background: F, border: `1.5px solid ${B}`, borderRadius: '1.2em', padding: '0.7em 1.2em', marginTop: '0.2em' }}>
          <svg width="1.4em" height="1.4em" viewBox="0 0 24 24" fill="none" stroke={M} strokeWidth="2.5" strokeLinecap="round"><circle cx="11" cy="11" r="7"/><line x1="16.5" y1="16.5" x2="21" y2="21"/></svg>
          <p style={t6}>{mock.searchModel}</p>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.7em', marginTop: '0.5em' }}>
          {tile('iPhone', true)}
          {tile('iPad')}
          {tile('MacBook')}
          {tile('Samsung')}
          {tile('Watch')}
          {tile('iMac')}
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: '0.5em', alignItems: 'center', justifyContent: 'center', marginBottom: '0.4em' }}>
          <div style={{ width: '0.6em', height: '0.6em', borderRadius: '50%', background: '#10b981' }} />
          <p style={t6}>{mock.modelsAvailable}</p>
        </div>
        <div style={{ background: D, borderRadius: '1.2em', height: '3.6em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ ...t9, color: '#fff' }}>{mock.continueLabel}</p>
        </div>
      </ScreenPad>
    </ScreenOverlay>
  </PhoneWrap>
);

const AnimatedStep2Illustration = ({ mock }: { mock: MockTexts }) => (
  <PhoneWrap>
    <img src="/illustrations/iphone-frame.svg" alt="" style={phoneFrame} draggable={false} />
    <ScreenOverlay>
      <ScreenPad>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.7em' }}>
          <p style={{ ...t6, fontWeight: 600, color: M, whiteSpace: 'nowrap' }}>{mock.stepTwoOfFour}</p>
          <div style={{ flex: 1, height: '0.6em', borderRadius: '0.3em', background: B, overflow: 'hidden' }}>
            <div style={{ width: '50%', height: '100%', background: D, borderRadius: '0.3em' }} />
          </div>
        </div>
        <p style={{ ...t11, marginTop: '0.4em' }}>{mock.screenCondition}</p>
        <p style={t7}>{mock.howScreenLooks}</p>
        {opt(mock.noScratches, mock.screenPerfect, true)}
        {opt(mock.lightScratches, mock.lightUsage)}
        {opt(mock.visibleDamage, mock.cracksDeepScratches)}
        <div style={{ flex: 1 }} />
        <div style={{ background: F, borderRadius: '1.2em', padding: '0.9em 1.2em' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <p style={t6}>{mock.estimatedValue}</p>
            <p style={t11}>€ 345</p>
          </div>
          <div style={{ display: 'flex', gap: '0.3em', marginTop: '0.5em' }}>
            {['Opslag', 'Scherm', 'Batterij', 'Staat'].map((s, i) => (
              <div key={s} style={{ flex: 1, height: '0.35em', borderRadius: '0.2em', background: i < 2 ? D : B }} />
            ))}
          </div>
        </div>
      </ScreenPad>
    </ScreenOverlay>
  </PhoneWrap>
);

const AnimatedStep3Illustration = ({ mock }: { mock: MockTexts }) => (
  <PhoneWrap>
    <img src="/illustrations/iphone-frame.svg" alt="" style={phoneFrame} draggable={false} />
    <ScreenOverlay>
      <ScreenPad>
        <div style={{ background: D, borderRadius: '1.4em', padding: '1.1em 1.4em', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <p style={{ ...t9, color: '#fff' }}>{mock.cart}</p>
            <p style={{ ...t6, color: 'rgba(255,255,255,0.5)' }}>{mock.twoDevices}</p>
          </div>
          <div style={{ width: '2.6em', height: '2.6em', borderRadius: '50%', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={t10}>2</p>
          </div>
        </div>
        {cartItem('iPhone 15 Pro', `128GB · ${mock.noScratches}`, '€ 345')}
        {cartItem('MacBook Air M2', `256GB · ${mock.excellent}`, '€ 580')}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5em', padding: '0.4em 0' }}>
          <svg width="1.2em" height="1.2em" viewBox="0 0 24 24" fill="none" stroke={M} strokeWidth="2.5" strokeLinecap="round"><line x1="12" y1="5" x2="12" y2="19"/><line x1="5" y1="12" x2="19" y2="12"/></svg>
          <p style={t6}>{mock.addAnotherDevice}</p>
        </div>
        <div style={{ borderTop: `1.5px solid ${B}` }} />
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4em', padding: '0.4em 0' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <p style={t6}>{mock.subtotal}</p>
            <p style={t6}>€ 925</p>
          </div>
          <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <p style={t8}>{mock.total}</p>
            <p style={t11}>€ 925</p>
          </div>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ background: D, borderRadius: '1.2em', height: '3.6em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <p style={{ ...t9, color: '#fff' }}>{mock.makeAppointment}</p>
        </div>
      </ScreenPad>
    </ScreenOverlay>
  </PhoneWrap>
);

const AnimatedStep4Illustration = ({ mock }: { mock: MockTexts }) => (
  <PhoneWrap>
    <img src="/illustrations/iphone-frame.svg" alt="" style={phoneFrame} draggable={false} />
    <ScreenOverlay>
      <ScreenPad style={{ alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ width: '6.5em', height: '6.5em', borderRadius: '50%', background: D, display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 0.4em 2em rgba(17,24,39,0.2)' }}>
          <svg width="3.2em" height="3.2em" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
        </div>
        <p style={{ ...t11, marginTop: '1.2em' }}>{mock.paymentSuccessful}</p>
        <p style={{ ...tx, fontSize: '2.4em', fontWeight: 800, color: D, marginTop: '0.15em' }}>€ 925</p>
        <p style={{ ...t7, marginTop: '0.2em' }}>{mock.paidToAccount}</p>
        <div style={{ width: '100%', background: F, borderRadius: '1.2em', padding: '1.1em 1.4em', marginTop: '1.4em', display: 'flex', flexDirection: 'column', gap: '0.7em' }}>
          {[[mock.orderNumber, '#PB-2024-1847'], [mock.method, mock.directPayout], [mock.speed, mock.sameDay]].map(([l, v]) => (
            <div key={l} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <p style={t6}>{l}</p>
              <p style={{ ...t8, fontWeight: 700 }}>{v}</p>
            </div>
          ))}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5em', marginTop: '0.7em' }}>
          <svg width="1.2em" height="1.2em" viewBox="0 0 24 24" fill="none" stroke={M} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="4" width="20" height="16" rx="2"/><path d="M22 7l-10 7L2 7"/></svg>
          <p style={t6}>{mock.emailConfirmation}</p>
        </div>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: '0.7em', width: '100%' }}>
          <div style={{ flex: 1, background: F, border: `1.5px solid ${B}`, borderRadius: '1.2em', height: '3.2em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ ...t8, fontWeight: 600 }}>{mock.homeButton}</p>
          </div>
          <div style={{ flex: 1, background: D, borderRadius: '1.2em', height: '3.2em', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <p style={{ ...t8, fontWeight: 600, color: '#fff' }}>{mock.shareButton}</p>
          </div>
        </div>
      </ScreenPad>
    </ScreenOverlay>
  </PhoneWrap>
);

const HowItWorksModal: React.FC<HowItWorksModalProps> = ({ isOpen, onClose, slides }) => {
  const { language } = useLanguage();
  const t = (key: any) => getTranslation(language as Language, key);
  const mockTexts = MOCK_TEXTS[language as Language] || MOCK_TEXTS.nl;

  // Ref to store scroll position - defined early so it can be used by unlockBody
  const scrollYRef = React.useRef(0);

  // Separate function to unlock body - can be called from multiple places
  const unlockBody = React.useCallback(() => {
    if (typeof document === 'undefined') return;

    const scrollY = scrollYRef.current;
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.width = '';
    document.body.style.overflow = '';
    document.documentElement.style.overflow = '';

    // Restore scroll position
    if (scrollY > 0) {
      window.scrollTo(0, scrollY);
    }
  }, []);

  const [isClosing, setIsClosing] = useState(false);

  // Wrapper for onClose with closing animation
  const handleClose = React.useCallback(() => {
    setIsClosing(true);
    setTimeout(() => {
      setIsClosing(false);
      onClose();
    }, 300);
  }, [onClose]);

  /* ── Custom UI illustraties die jullie eigen module tonen ── */
  const S = { dark: '#111827', mid: '#1e293b', border: '#e2e8f0', bg: '#f8fafc', light: '#f1f5f9', txt: '#0f172a', muted: '#64748b', green: '#10b981', blue: '#3b82f6', white: '#ffffff' };

  const defaultSlides = useMemo(() => [
    {
      /* STAP 1 — Phone Bros device selectie scherm — pixel-perfect */
      illustration: <AnimatedStep1Illustration mock={mockTexts} />,
      title: t('step1Title') || 'Selecteer je apparaat',
      description: t('step1ModalDescription') || 'Kies je merk en model uit ons uitgebreide assortiment. We ondersteunen alle populaire Apple en Samsung apparaten.',
      details: [
        t('step1ModalDetail1') || 'Selecteer je apparaattype (iPhone, Samsung, iPad, MacBook, Apple Watch of Mac)',
        t('step1ModalDetail2') || 'Kies het exacte model dat je wilt verkopen',
        t('step1ModalDetail3') || 'Bekijk direct een geschatte prijsindicatie',
        t('step1ModalDetail4') || 'Voeg meerdere apparaten toe aan je winkelwagen'
      ],
      helper: t('instantValuation') || 'Directe prijsindicatie'
    },
    {
      /* STAP 2 — Conditiecheck: telefoon + vraagopties zoals in QuestionStep */
      illustration: <AnimatedStep2Illustration mock={mockTexts} />,
      title: t('step2Title') || 'Beschrijf de conditie',
      description: t('step2ModalDescription') || 'Beantwoord een paar korte vragen over je apparaat. Ons systeem berekent direct de meest accurate prijs op basis van je antwoorden.',
      details: [
        t('step2ModalDetail1') || 'Geef de opslagcapaciteit op (bijv. 64GB, 128GB, 256GB)',
        t('step2ModalDetail2') || 'Beschrijf de batterijconditie en algehele staat',
        t('step2ModalDetail3') || 'Beantwoord apparaatspecifieke vragen (scherm, behuizing, etc.)',
        t('step2ModalDetail4') || 'Zie direct hoe je antwoorden de uiteindelijke prijs beïnvloeden'
      ],
      helper: t('professionalQualityControl') || 'Professionele kwaliteitscontrole'
    },
    {
      /* STAP 3 — Verkoopmandje: cart items met device + prijs */
      illustration: <AnimatedStep3Illustration mock={mockTexts} />,
      title: t('step4Title') || 'Controleer je winkelwagen',
      description: t('step4ModalDescription') || 'Bekijk alle apparaten die je wilt verkopen. Pas hoeveelheden aan, verwijder items of voeg meer toe aan je winkelwagen.',
      details: [
        t('step4ModalDetail1') || 'Bekijk alle apparaten die je hebt toegevoegd',
        t('step4ModalDetail2') || 'Pas hoeveelheden aan per apparaat',
        t('step4ModalDetail3') || 'Controleer de totale geschatte waarde',
        t('step4ModalDetail4') || 'Ga verder naar de volgende stap wanneer je klaar bent'
      ],
      helper: t('step4Helper') || 'Transparante prijzen'
    },
    {
      /* STAP 4 — Direct uitbetaald: succes-scherm met vinkje + ordernr */
      illustration: <AnimatedStep4Illustration mock={mockTexts} />,
      title: t('step3Title') || 'Krijg je geld',
      description: t('step3ModalDescription') || 'Kies voor ophalen of kom langs in de winkel. Na controle krijg je direct je geld uitbetaald.',
      details: [
        t('step3ModalDetail1') || 'Plan een afspraak in de winkel of kies voor gratis ophaalservice',
        t('step3ModalDetail2') || 'Wij controleren je apparaat volgens professionele standaarden',
        t('step3ModalDetail3') || 'Ontvang direct je geld na controle en goedkeuring',
        t('step3ModalDetail4') || 'Veilig, snel en betrouwbaar - zonder gedoe of wachttijd'
      ],
      helper: t('satisfiedCustomers') || '8.000+ tevreden klanten'
    }
  ], [language, t]);

  const data = slides && slides.length ? slides : defaultSlides;
  const [currentIndex, setCurrentIndex] = useState(0);

  // Confetti on step 4
  const [showConfetti, setShowConfetti] = useState(false);
  const [recycleConfetti, setRecycleConfetti] = useState(true);
  const imageSectionRef = React.useRef<HTMLDivElement>(null);
  const [confettiDims, setConfettiDims] = useState({ w: 300, h: 400 });

  useEffect(() => {
    if (currentIndex === data.length - 1) {
      if (imageSectionRef.current) {
        setConfettiDims({ w: imageSectionRef.current.offsetWidth, h: imageSectionRef.current.offsetHeight });
      }
      setShowConfetti(true);
      setRecycleConfetti(true);
      const t = setTimeout(() => setRecycleConfetti(false), 4000);
      return () => clearTimeout(t);
    }
    setShowConfetti(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIndex]);

  // Swipe to dismiss on mobile using framer-motion
  const slideContainerRef = React.useRef<HTMLDivElement>(null);
  const headerRef = React.useRef<HTMLDivElement>(null);
  const y = useMotionValue(0);
  const overlayOpacityValue = useMotionValue(1);
  const overlayOpacityTransform = useTransform(y, [0, 300], [1, 0]);
  const [isDraggingAllowed, setIsDraggingAllowed] = useState(false);
  const [isScrolling, setIsScrolling] = useState(false);
  const dragStartYRef = React.useRef<number | null>(null);
  const isMobile = typeof window !== 'undefined' && window.innerWidth <= 768;

  // Scroll lock when modal is open (wheel event method)
  useEffect(() => {
    if (!isOpen) return;
    const onWheel = (e: WheelEvent) => {
      const modal = (e.target as HTMLElement)?.closest('[data-modal-overlay]');
      if (!modal) e.preventDefault();
    };
    window.addEventListener('wheel', onWheel, { passive: false });
    return () => window.removeEventListener('wheel', onWheel);
  }, [isOpen]);

  // Track scrolling to prevent drag during scroll
  useEffect(() => {
    const container = slideContainerRef.current;
    if (!container || !isMobile) return;

    let scrollTimer: NodeJS.Timeout | null = null;
    let lastScrollTop = container.scrollTop;
    let touchStartY: number | null = null;
    let isTouchActive = false;

    const handleTouchStart = (e: TouchEvent) => {
      touchStartY = e.touches[0].clientY;
      isTouchActive = true;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (!touchStartY || !isTouchActive) return;

      const touchY = e.touches[0].clientY;
      const deltaY = touchY - touchStartY;

      // If significant vertical movement, check if it's scroll or drag
      if (Math.abs(deltaY) > 10) {
        const currentScrollTop = container.scrollTop;
        const scrollDelta = Math.abs(currentScrollTop - lastScrollTop);

        // If container is scrolling, it's a scroll gesture, not drag
        if (scrollDelta > 5 || currentScrollTop > 0) {
          setIsScrolling(true);
          setIsDraggingAllowed(false);
          y.set(0);

          // Clear existing timeout
          if (scrollTimer) {
            clearTimeout(scrollTimer);
          }

          // Reset scrolling flag after scroll ends - longer delay to prevent flicker
          scrollTimer = setTimeout(() => {
            setIsScrolling(false);
          }, 400);
        }
      }

      lastScrollTop = container.scrollTop;
    };

    const handleTouchEnd = () => {
      isTouchActive = false;
      touchStartY = null;
    };

    const handleScroll = () => {
      const currentScrollTop = container.scrollTop;
      const scrollDelta = Math.abs(currentScrollTop - lastScrollTop);

      // If significant scroll movement, disable drag
      if (scrollDelta > 5) {
        setIsScrolling(true);
        setIsDraggingAllowed(false);

        // Cancel any active drag
        y.set(0);

        // Clear existing timeout
        if (scrollTimer) {
          clearTimeout(scrollTimer);
        }

        // Reset scrolling flag after scroll ends - longer delay to prevent flicker
        scrollTimer = setTimeout(() => {
          setIsScrolling(false);
        }, 400);
      }

      lastScrollTop = currentScrollTop;
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('touchstart', handleTouchStart, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });
    container.addEventListener('touchend', handleTouchEnd, { passive: true });

    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('touchstart', handleTouchStart);
      container.removeEventListener('touchmove', handleTouchMove);
      container.removeEventListener('touchend', handleTouchEnd);
      if (scrollTimer) {
        clearTimeout(scrollTimer);
      }
    };
  }, [y, isMobile]);

  useEffect(() => {
    if (!isOpen) {
      // Reset modal state when closed
      setTimeout(() => setCurrentIndex(0), 300);
      y.set(0);
      overlayOpacityValue.set(1);
      setIsDraggingAllowed(false);
      setIsScrolling(false);
    }
  }, [isOpen, y, overlayOpacityValue]);

  const goToSlide = (index: number) => {
    if (index < 0 || index >= data.length) return;
    setCurrentIndex(index);
  };

  const nextSlide = () => {
    if (currentIndex < data.length - 1) {
      setCurrentIndex(prev => prev + 1);
    }
  };

  const prevSlide = () => {
    if (currentIndex > 0) {
      setCurrentIndex(prev => prev - 1);
    }
  };

  // Sync overlay opacity with y position
  useMotionValueEvent(overlayOpacityTransform, 'change', (latest) => {
    overlayOpacityValue.set(latest);
  });

  const handleDragStart = (event: any) => {
    // Don't allow drag if currently scrolling or recently scrolled
    if (isScrolling) {
      setIsDraggingAllowed(false);
      y.set(0);
      return false;
    }

    // Only allow drag if at top of scrollable content AND dragging starts from header area
    if (slideContainerRef.current && headerRef.current) {
      const isAtTop = slideContainerRef.current.scrollTop === 0;

      if (!isAtTop || !isMobile) {
        setIsDraggingAllowed(false);
        y.set(0);
        return false;
      }

      // Check if drag started from header area - use the point from the event
      const point = event.point || { y: event.clientY || 0 };
      const headerRect = headerRef.current.getBoundingClientRect();
      // Only allow drag from header area (plus small buffer)
      const isInHeaderArea = point.y >= headerRect.top && point.y <= headerRect.bottom + 20;

      if (isInHeaderArea) {
        setIsDraggingAllowed(true);
        dragStartYRef.current = point.y;
        return true;
      }
    }

    setIsDraggingAllowed(false);
    y.set(0);
    return false;
  };

  const handleDrag = (_event: any, info: PanInfo) => {
    // Only allow downward dragging and only if dragging is allowed
    if (!isDraggingAllowed || isScrolling) {
      // Cancel drag if not allowed or if scrolling started
      y.set(0);
      setIsDraggingAllowed(false);
      return;
    }

    // Only allow downward movement
    if (info.offset.y > 0) {
      y.set(info.offset.y);
    } else {
      // Prevent upward drag
      y.set(0);
    }
  };

  const handleDragEnd = (_event: any, info: PanInfo) => {
    if (!isDraggingAllowed) {
      y.set(0);
      overlayOpacityValue.set(1);
      dragStartYRef.current = null;
      return;
    }

    const threshold = 100; // Minimum distance to close
    const velocityThreshold = 500; // Minimum velocity to close

    if (info.offset.y > threshold || info.velocity.y > velocityThreshold) {
      // Close modal with smooth animation
      handleClose();
    } else {
      // Snap back to original position with spring animation
      y.set(0);
      overlayOpacityValue.set(1);
    }
    setIsDraggingAllowed(false);
    dragStartYRef.current = null;
  };

  const currentSlide = data[currentIndex];

  const modalVariants = isMobile
    ? {
      hidden: { y: '100%', opacity: 1 },
      visible: { y: 0, opacity: 1, transition: { type: 'tween' as const, duration: 0.25, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] } },
    }
    : {
      hidden: { y: 12, opacity: 0, scale: 0.98 },
      visible: { y: 0, opacity: 1, scale: 1, transition: { type: 'tween' as const, duration: 0.2, ease: [0.25, 0.1, 0.25, 1] as [number, number, number, number] } },
    };

  const overlayVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1 },
  };

  // Use overlayOpacityValue only on mobile when dragging, otherwise use variants
  // Don't override opacity if not dragging, let variants handle it
  const overlayStyle = isMobile && isDraggingAllowed && y.get() > 0
    ? { opacity: overlayOpacityValue }
    : undefined;

  // Early return if not open - ensures modal is removed from DOM
  if (!isOpen) {
    return null;
  }

  return (
    <Overlay
      data-modal-overlay
      $closing={isClosing}
      onClick={handleClose}
    >
      <ModalCard
        $closing={isClosing}
        onClick={(e) => e.stopPropagation()}
      >
        <Header ref={headerRef}>
          <Heading>{t('howItWorks') || 'Hoe het werkt'}</Heading>
          <CloseButton onClick={handleClose} aria-label={t('close') || 'Sluiten'}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"></line>
              <line x1="6" y1="6" x2="18" y2="18"></line>
            </svg>
          </CloseButton>
        </Header>

        <SlideContainer ref={slideContainerRef} data-slide-container>
          <SlideContent>
            <ImageSection ref={imageSectionRef} style={{ position: 'relative', overflow: 'hidden' }}>
              {showConfetti && (
                <Confetti
                  width={confettiDims.w}
                  height={confettiDims.h}
                  recycle={recycleConfetti}
                  numberOfPieces={120}
                  gravity={0.12}
                  initialVelocityY={10}
                  onConfettiComplete={() => setShowConfetti(false)}
                  style={{ position: 'absolute', top: 0, left: 0, zIndex: 5, pointerEvents: 'none' }}
                />
              )}
              <ImageWrapper>
                {'illustration' in currentSlide && currentSlide.illustration
                  ? currentSlide.illustration
                  : 'image' in currentSlide && currentSlide.image
                    ? <Image src={currentSlide.image} alt={currentSlide.title} />
                    : null
                }
              </ImageWrapper>
            </ImageSection>
            <ContentSection>
              <StepBadge>
                {t('stepOf')?.replace('{current}', String(currentIndex + 1)).replace('{total}', String(data.length)) || ('Stap ' + String(currentIndex + 1) + ' van ' + String(data.length))}
              </StepBadge>
              <Title>{currentSlide.title}</Title>
              <Description>{currentSlide.description}</Description>
              {currentSlide.details && currentSlide.details.length > 0 && (
                <DetailsList>
                  {currentSlide.details.map((detail, idx) => (
                    <DetailItem key={idx}>
                      <DetailIcon>
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                          <polyline points="20 6 9 17 4 12"></polyline>
                        </svg>
                      </DetailIcon>
                      <span>{detail}</span>
                    </DetailItem>
                  ))}
                </DetailsList>
              )}
              {currentSlide.helper && (
                <HelperText>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                    <polyline points="22 4 12 14.01 9 11.01"></polyline>
                  </svg>
                  {currentSlide.helper}
                </HelperText>
              )}
            </ContentSection>
          </SlideContent>
        </SlideContainer>

        <NavigationContainer>
          <StepIndicators>
            {data.map((_, index) => (
              <StepDot
                key={index}
                $active={index === currentIndex}
                onClick={() => goToSlide(index)}
                aria-label={t('goToStep').replace('{step}', String(index + 1))}
              />
            ))}
          </StepIndicators>
          <NavButtons>
            <NavButton
              $disabled={currentIndex === 0}
              onClick={prevSlide}
              aria-label={t('previous') || 'Vorige stap'}
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
              {t('previous') || 'Vorige'}
            </NavButton>
            {currentIndex === data.length - 1 ? (
              <NavButton
                $primary
                onClick={handleClose}
                aria-label={t('close') || 'Sluiten'}
              >
                {t('close') || 'Sluiten'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14" />
                  <path d="M12 5l7 7-7 7" />
                </svg>
              </NavButton>
            ) : (
              <NavButton
                $primary
                onClick={nextSlide}
                aria-label={t('next') || 'Volgende stap'}
              >
                {t('next') || 'Volgende'}
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M9 18l6-6-6-6" />
                </svg>
              </NavButton>
            )}
          </NavButtons>
        </NavigationContainer>
      </ModalCard>
    </Overlay>
  );
};

export default HowItWorksModal;
